import { AuthenticationService } from './../../services/authentication.service';
import { Injectable } from '@angular/core';
import DynamicsWebApi, { RetrieveMultipleRequest } from 'dynamics-web-api'
import { AuthenticationDataService } from '../authentication/authentication.service';
import { httpGate } from '../../utility/http-semaphore';

function delay(t) {
  return new Promise<void>((resolve) => {
      setTimeout(()=> resolve(), t);
    });
}
function handle429(callback:()=>Promise<any>){
  return ((err)=> {
    if(err && err.status && err.status==429){
      const waitTime = (err.headers['retry-after']) ? parseInt(err.headers['retry-after']) : 1000;
      console.log('call failed with 429, retrying after ', waitTime);
      return delay(waitTime).then(()=>callback())
    }
    else{
      return Promise.reject(err);
    }
  })
}
@Injectable({
  providedIn: 'root'
})
export class DynamicsClientService  {

  private _dwa: DynamicsWebApi;
  /**
   * True between startBatch() and executeBatch(). While batching, per-request
   * methods (createRequest/update/create/…) DO NOT hit the network — DynamicsWebApi
   * only queues them and returns a promise that never settles (it is resolved
   * only via executeBatch). Routing those through `httpGate` would therefore
   * acquire a concurrency permit that is never released, leaking one slot per
   * queued sub-request until the global semaphore deadlocks app-wide (OMNI-57234:
   * SDR submit retries exhaust the gate, leaving submit stuck "buffering" with no
   * network call and no error until a reload). So while batching we bypass the
   * gate; only executeBatch — the single real network call — consumes a permit.
   */
  private _inBatch = false;
  get dwa() {
    return this._dwa || this.getDwa({
      returnRepresentation : true,
      includeAnnotations:'*'
    });
  }

  /**
   * Gate a real network call through the global HTTP semaphore — UNLESS a batch
   * is being assembled, in which case the call only queues a sub-request (no I/O,
   * never-settling promise) and must not hold a permit. See `_inBatch`.
   */
  private gate<T>(fn: () => Promise<T>): Promise<T> {
    return this._inBatch ? fn() : httpGate(fn);
  }

  private getDwa(config?: DynamicsWebApi.Config) {
    return new DynamicsWebApi({
      webApiUrl:`${this.authService.userConfig.activeInstance.url}/api/data/v9.1/`,
      onTokenRefresh: tokenRefresh => this.authDataService.getTokenForSelectedInstance().then(at => at.token).then(tokenRefresh),
      ...config ? config : {},
    })
  }

  constructor(
    public authService: AuthenticationService,
    public authDataService: AuthenticationDataService
  ) {
    // super({
    //   returnRepresentation : true,
    //   webApiUrl:`${authService.userConfig.activeInstance.url}/api/data/v9.1/`,
    //   onTokenRefresh: tokenRefresh => tokenRefresh(authService.access)
    // });
  }

  async executeFetchQuery(entityName: string, fetchXML:string, includeAnnotations?: string): Promise<any>;
  async executeFetchQuery<T>(entityName: string, fetchXML:string, includeAnnotations?: string): Promise<T[]>;
  async executeFetchQuery(entityName: string, fetchXML:string, includeAnnotations: string = '*') {
    return httpGate(async () => {
      let response
      let newDynamicsConnection = new DynamicsWebApi({
        returnRepresentation : true,
        webApiUrl:`${this.authService.userConfig.activeInstance.url}/api/data/v9.1/`,
        onTokenRefresh: tokenRefresh => this.authDataService.getTokenForSelectedInstance().then(accessToken => accessToken.token).then(tokenRefresh),
        includeAnnotations: includeAnnotations
      })
      fetchXML = fetchXML.replace(/\s+/g, ' ').trim();
      response = await newDynamicsConnection.fetchAll(entityName, fetchXML)
                    .catch(handle429(()=>newDynamicsConnection.fetchAll(entityName, fetchXML)))
                    .then(res => res.value)
      return response;
    });
  }

  async executeFetchQueryWithPageNumber(entityName: string, fetchXML:string,pageCount,includeAnnotations: string = '*'){
    return httpGate(async () => {
      let response
      let newDynamicsConnection = new DynamicsWebApi({
        returnRepresentation : true,
        webApiUrl:`${this.authService.userConfig.activeInstance.url}/api/data/v9.1/`,
        onTokenRefresh: tokenRefresh => this.authDataService.getTokenForSelectedInstance().then(accessToken => accessToken.token).then(tokenRefresh),
        includeAnnotations: includeAnnotations
      })
      fetchXML = fetchXML.replace(/\s+/g, ' ').trim();
      response = await newDynamicsConnection.fetch(entityName, fetchXML,includeAnnotations,pageCount)
                        .catch(handle429(()=>newDynamicsConnection.fetch(entityName, fetchXML,includeAnnotations,pageCount)))
                        .then(res => res.value)
      return response;
    });
  }

  async executeFetchXml<T>(entityName: string, fetchXml: string, includeAnnotations: string = '*', pageNumber?: number, pagingCookie?: string): Promise<DynamicsWebApi.FetchXmlResponse<T>> {
    return httpGate(async () => {
      let newDynamicsConnection = new DynamicsWebApi({
        returnRepresentation : true,
        webApiUrl:`${this.authService.userConfig.activeInstance.url}/api/data/v9.1/`,
        onTokenRefresh: tokenRefresh => this.authDataService.getTokenForSelectedInstance().then(accessToken => accessToken.token).then(tokenRefresh),
        includeAnnotations: includeAnnotations
      });
      fetchXml = fetchXml.replace(/\s+/g, ' ').trim();
      return await newDynamicsConnection.fetch(entityName, fetchXml, includeAnnotations, pageNumber, pagingCookie);
    });
  }

  // async executeWebApi(guidKey: string, entityToSearch: string){
  //   let response
  //   //newDynamicsConnection.setConfig()
  //   // https://io-dev.crm3.dynamics.com/api/data/v9.1/GlobalOptionSetDefinitions(4bff62ec-95b4-4c16-a398-4d582e776d40)
  //   // response = await newDynamicsConnection.retrieve("4bff62ec-95b4-4c16-a398-4d582e776d40","GlobalOptionSetDefinitions");
  //   response = await this.dwa.retrieve(guidKey,entityToSearch);
  //   return response;
  // }


  async retrieveAll<T = any>(collection: string, select?: string[], filter?: string){
    return this.gate(() =>
      this.dwa.retrieveAll<T>(collection, select, filter)
                .catch(handle429(()=>this.dwa.retrieveAll<T>(collection, select, filter)))
    );
  }

  async retrieveAllRequest<T = any>(request: RetrieveMultipleRequest): Promise<DynamicsWebApi.RetrieveMultipleResponse<T>> {
    return this.gate(() =>
      this.dwa.retrieveAllRequest(request)
                .catch(handle429(()=>this.dwa.retrieveAllRequest(request)))
    );
  }

  async retrieveMultipleRequest<T = any>(request: RetrieveMultipleRequest): Promise<DynamicsWebApi.RetrieveMultipleResponse<T>> {
    return this.gate(() =>
      this.dwa.retrieveMultipleRequest(request)
                .catch(handle429(()=>this.dwa.retrieveMultipleRequest(request)))
    );
  }

  async retrieve<T = any>(entityID: string, collection: string, select: string[]){
    return this.gate(() =>
      this.dwa.retrieve<T>(entityID, collection, select)
                .catch(handle429(()=>this.dwa.retrieve<T>(entityID, collection, select)))
    );
  }

  async retrieveGlobalOptionSet(globalOptionSetKey: string, castType?: string, select?: string[]): Promise<any>{
    return this.gate(() =>
      this.dwa.retrieveGlobalOptionSet(globalOptionSetKey, castType, select)
    );
  }

  async retrieveAttributes(entity: string, type?: string, select?: string[], filter?, expand?): Promise<any>{
    return this.gate(() =>
      this.dwa.retrieveAttributes(entity, type, select, filter, expand)
    );
  }

  async update(key?: string, collection?: string, object?, prefer?) {
    return this.gate(() =>
      this.dwa.update(key, collection, object, prefer)
    );
  }

  async create<T = any>(object?, collection?: string, prefer?, select?) {
    return this.gate(async () =>
      <T>(await this.dwa.create(object, collection, prefer, select))
    );
  }

  delete(key: string, collection: string, propertyName?: string) {
    return this.gate(() =>
      this.dwa.deleteRecord(key, collection, propertyName)
    );
  }

  /**
   * @param config Optional DynamicsWebApi config overrides for the batch DWA instance.
   * IMPORTANT: By default (no config) the batch DWA instance is created without
   * `includeAnnotations`, so batch responses do NOT carry `@OData.Community.Display.V1.FormattedValue`
   * keys. Pass `{ returnRepresentation: true, includeAnnotations: '*' }` when the batch results
   * will be used to rebuild in-memory model objects that depend on formatted values (e.g. MI sync).
   */
  startBatch(config?: DynamicsWebApi.Config) {
    this._dwa = this.getDwa(config);
    this._inBatch = true;
    this.dwa.startBatch();
  }

  /**
   * @param options.continueOnError Sets the `Prefer: odata.continue-on-error`
   * header. By default a Dynamics changeset is one atomic transaction — any
   * sub-request failure rolls back every other request in the same batch.
   * With this set, each request's outcome is independent: a rejected
   * sub-request comes back as an Error at its own position in the results
   * array (see dynamics-web-api's parseBatchResponse) without sinking its
   * siblings. Use for a batch whose requests must succeed/fail independently
   * (e.g. per-line create results); omit for a batch that must be all-or-
   * nothing (e.g. _createSdrBatch's fresh SDR create).
   */
  executeBatch(options?: { continueOnError?: boolean }) {
    return httpGate(() => {
      try {
        // try/finally clears batch state on EVERY exit (normal return, sync throw
        // while building the body, or async rejection). Leaving _inBatch set
        // silently disables httpGate for all later DWA calls until reload (OMNI-57234).
        return this.dwa.executeBatch(options);
      } finally {
        this._dwa = undefined;
        this._inBatch = false;
      }
    });
  }

  /**
   * Abandon an in-progress batch without sending it, restoring gate state. Call
   * from a caller's error path when a throw before executeBatch() means it will
   * never run — otherwise _inBatch stays true and bypasses httpGate (OMNI-57234).
   * No-op (safe) if executeBatch() already cleared state.
   */
  abortBatch() {
    this._dwa = undefined;
    this._inBatch = false;
  }

  async createRequest(request: DynamicsWebApi.CreateRequest) {
    return this.gate(() =>
      this.dwa.createRequest(request)
    );
  }

  async executeUnboundAction<T = any>(actionName: string, requestObject?: any) {
    return this.gate(async () =>
      <T>(await this.dwa.executeUnboundAction(actionName, requestObject))
    );
  }

  async executeUnboundFunction<T = any>(functionName: string, requestObject?: any) {
    return this.gate(async () =>
      <T>(await this.dwa.executeUnboundFunction(functionName, requestObject))
    );
  }

  async retrieveRequest(request: DynamicsWebApi.RetrieveRequest) {
    return this.gate(() =>
      this.dwa.retrieveRequest(request)
    );
  }

  async deleteSinglePropertyValue(id,entityName,property){
    return this.gate(() =>
      this.dwa.deleteRecord(id,entityName,property)
    );
  }
}
